import { areObjectIdsEqual, isObjectIdString } from 'helpers/idComparison';
import type { ProjectInfo } from 'setup/types/core';
import { isProjectBroadcastModel } from 'shared/domain/project/projectBroadcastGuards';
import type { ProjectModel } from 'shared/domain/project/types/model';
import { ISO6391LanguageCode } from 'shared/types/locale';

export function createInitialProjectState(): ProjectInfo {
  return {
    _id: '',
    organizationId: '',
    code: '',
    name: '',
    processes: [],
    error: null,
    userHasAccess: false,
    timezone: '',
    budget: 0,
    currency: '',
    projectStartDate: null,
    projectEndDate: null,
    localeCode: ISO6391LanguageCode.en,
    pdfContactFooter: undefined,
    pdfFooter: undefined,
    pdfHeader: undefined,
  };
}

export function mergeProjectPayload(
  payload: Partial<ProjectInfo>,
  current: ProjectInfo
): ProjectInfo {
  return {
    ...current,
    ...payload,
    processes: payload.processes ?? current.processes,
  };
}

export function patchProjectMetadata(
  state: ProjectInfo,
  project: ProjectModel
): ProjectInfo {
  if (state._id !== project._id) {
    return state;
  }
  return {
    ...state,
    name: project.name ?? state.name,
    organizationId: project.organizationId ?? state.organizationId,
    timezone: project.timezone ?? state.timezone,
    budget: project.budget ?? state.budget,
    currency: project.currency ?? state.currency,
    localeCode: project.localeCode ?? state.localeCode,
  };
}

/**
 * The selected project as the store keeps it: the fields the page reads, as
 * `patchProjectMetadata` refreshes them. Its processes come from the process
 * store, not from the project's process ids.
 */
export function projectModelToProjectInfo({
  _id,
  organizationId,
  name,
  timezone,
  budget,
  currency,
  localeCode,
}: ProjectModel): Partial<ProjectInfo> {
  return { _id, organizationId, name, timezone, budget, currency, localeCode };
}

/** What a broadcast of the worker's selected project means for the store. */
export enum SelectedProjectChange {
  /** The same project: its name, timezone, … may have changed. */
  refreshMetadata = 'refreshMetadata',
  /** Another project was selected (here or in another tab). */
  switchTo = 'switchTo',
  /** No project to switch to, or the same one without its details. */
  ignore = 'ignore',
}

export function classifySelectedProjectBroadcast(
  project: ProjectModel | undefined,
  currentProjectId: string
): SelectedProjectChange {
  if (areObjectIdsEqual(project?._id, currentProjectId)) {
    return isProjectBroadcastModel(project) ? SelectedProjectChange.refreshMetadata : SelectedProjectChange.ignore;
  }
  // An empty selection never wipes the page's project (e.g. the worker answered
  // before its project list was downloaded); with none set there is nothing to clear.
  if (!isObjectIdString(project?._id)) {
    return SelectedProjectChange.ignore;
  }
  return SelectedProjectChange.switchTo;
}
